import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useDelayedLoading } from './useDelayedLoading.ts';

export type AsyncStatus = 'loading' | 'ready' | 'unavailable';

export interface AsyncData<T> {
  /** Held at `'loading'` while the loader is on screen, so it is never cut
   *  short (§ `useDelayedLoading`) — render from this, not from the request. */
  status: AsyncStatus;
  data: T;
  /** Debounced view of `status === 'loading'` — wire straight into `LoadingState`. */
  showLoading: boolean;
  /** A load past the first is in flight (e.g. a filter changed) — the page
   *  already has content on screen, so this drives an inline busy indicator
   *  rather than the full-screen `LoadingState`. */
  refreshing: boolean;
  /** Re-runs `load`. Pass `{ force: true }` (an explicit "Tentar novamente")
   *  to show the full-screen loading state even if this isn't the first load. */
  reload: (options?: { force?: boolean }) => void;
  setData: Dispatch<SetStateAction<T>>;
}

/**
 * The one place a backend-driven page tracks "is this loaded yet, and should
 * the loader actually be shown". Runs `load` on mount and whenever `deps`
 * change, exposing `status`/`data` the way every such page needs, with
 * `showLoading` already debounced (§ `useDelayedLoading`) so a fast response
 * never flashes a loader.
 *
 * Every load is stamped with a ticket, so a response that arrives after a
 * newer request started — a slow page-1 landing after a fast page-2 — is
 * dropped rather than overwriting it.
 */
export function useAsyncData<T>(
  load: () => Promise<T>,
  initialValue: T,
  deps: unknown[] = [],
  delayMs?: number,
): AsyncData<T> {
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [data, setData] = useState<T>(initialValue);
  const [refreshing, setRefreshing] = useState(false);

  const loadRef = useRef(load);
  loadRef.current = load;
  const hasLoaded = useRef(false);
  const ticket = useRef(0);

  const reload = useCallback((options?: { force?: boolean }) => {
    const mine = ++ticket.current;
    if (options?.force || !hasLoaded.current) {
      setStatus('loading');
      setRefreshing(false);
    } else {
      setRefreshing(true);
    }

    loadRef.current().then(
      (result) => {
        if (mine !== ticket.current) return;
        hasLoaded.current = true;
        setData(result);
        setStatus('ready');
        setRefreshing(false);
      },
      () => {
        if (mine !== ticket.current) return;
        setStatus('unavailable');
        setRefreshing(false);
      },
    );
  }, []);

  // `deps` is caller-controlled, deliberately not `reload` itself.
  useEffect(() => {
    reload();
  }, deps);

  const showLoading = useDelayedLoading(status === 'loading', delayMs);

  return {
    status: showLoading ? 'loading' : status,
    data,
    showLoading,
    refreshing,
    reload,
    setData,
  };
}
